/**
 * ProductEditor — the mode-agnostic product editor shell (Task F1,
 * admin-product-parity). Extracted from the seller-only
 * `seller-dashboard/ProductEditorPage.tsx` (EPIC-006 Phase 7; PIM Wave P2) so
 * both the seller self-service surface and the admin on-behalf-of surface can
 * share one implementation, driven by an `EditorPort` (see `./port.ts`).
 *
 * Two-column on desktop (form left, `LivePreview` right) / stacked on mobile.
 * Sections: Basics / Pricing / Stock / Attributes / Photos / Variants / Tags.
 *
 * PIM Wave P2 additions (existing sections + flows unchanged):
 *   - The single category `<select>` becomes `CategoryMultiPicker` (primary +
 *     extras). The primary is kept in the RHF form as `category_id` (reusing
 *     the existing required validation); extras live in local state.
 *   - A schema-driven **Attributes** section (`DynamicAttributeForm`) renders
 *     from `GET /marketplace/categories/{primary}/form-schema`; it re-fetches
 *     on primary change, carrying entered values forward where the field slug
 *     survives (`carryOverValues`).
 *   - A header `CompletenessMeter` (live estimate of the backend score).
 *   - A footer whose shape is driven by `capabilities.statusControl`:
 *     'submit-review' is the seller's two-action footer ("Save draft" / status
 *     unchanged — no gate — and "Submit for review" — status →
 *     pending_approval, disabled-with-reason when required attributes are
 *     missing client-side; the server 422 is authority); 'direct' is a single
 *     Save action plus whatever `capabilities.renderStatusControl` supplies.
 *   - Structured PIM 422s (`pim.attribute_invalid` / `pim.required_missing`)
 *     map to per-field inline errors, never a bare toast.
 *
 * Auth: a 403 from the product load redirects to
 * `capabilities.forbiddenRedirectTo` (when the surface sets one) + toast —
 * the route guard is optimistic. Surfaces with no meaningful redirect target
 * (e.g. admin, already RBAC-gated at the route level) leave it undefined.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Icon } from "@garden/ui";

import { ThemeToggle } from "@/components/ThemeToggle";

import {
  parsePimError,
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  type StockState,
} from "@/features/seller-dashboard/api";
import type {
  EditorCreateBody,
  EditorPort,
  EditorProduct,
  EditorWriteBody,
} from "@/features/product-editor/port";
import { normaliseDecimal } from "@/features/product-editor/viewModel";
import {
  PRODUCT_UNIT_SLUGS,
  productEditorSchema,
  type ProductEditorForm,
} from "@/features/seller-dashboard/schemas";
import { PricingFields } from "@/features/seller-dashboard/PricingFields";
import { StockStateField } from "@/features/seller-dashboard/StockStateField";
import { VariantEditor } from "@/features/seller-dashboard/VariantEditor";
import { LivePreview } from "@/features/seller-dashboard/LivePreview";
import {
  PhotoUploader,
  type ReadyPhoto,
} from "@/features/seller-dashboard/PhotoUploader";
import {
  PhotoReorderList,
  type EditorPhoto,
} from "@/features/seller-dashboard/PhotoReorderList";
import { CategoryMultiPicker } from "@/features/seller-dashboard/CategoryMultiPicker";
import { DynamicAttributeForm, type DynamicAttributeFormHandle } from "@/features/seller-dashboard/attributes/DynamicAttributeForm";
import { CompletenessMeter } from "@/features/seller-dashboard/attributes/CompletenessMeter";
import { useFormSchema } from "@/features/seller-dashboard/attributes/useFormSchema";
import {
  carryOverValues,
  initialFormValues,
  toAttributePayload,
  type AttributeFormValues,
  type AttributeValueIn,
  type AttributeValueOut,
} from "@/features/seller-dashboard/attributes/formSchema";
import { visibleFieldSlugs } from "@/features/seller-dashboard/attributes/visibility";
import { requiredMissingFields } from "@/features/seller-dashboard/attributes/completeness";
import {
  categoryDisplayName,
  findNodeInTree,
  useCategoryTree,
} from "@/features/marketplace/useCategoryTree";
import { SuggestDialog } from "@/features/seller-dashboard/SuggestDialog";
import { ApiError } from "@/lib/api-client";

/** Narrow a server `unit` to the form's enum.
 *
 * An unrecognised slug falls back to "not stated" rather than breaking the
 * form. That case should not arise — the API validates on write and the CHECK
 * constraint backs it — but a product predating the enum, or one written by a
 * future version, must still be editable rather than crash the page. */
function asUnitSlug(value: string | null | undefined): ProductEditorForm["unit"] {
  return PRODUCT_UNIT_SLUGS.includes(value as (typeof PRODUCT_UNIT_SLUGS)[number])
    ? (value as ProductEditorForm["unit"])
    : "";
}

const DEFAULTS: ProductEditorForm = {
  name: "",
  description: "",
  category_id: "",
  status: "draft",
  stock_state: "unknown",
  price_kgs: "",
  bulk_price_kgs: "",
  bulk_threshold: "",
  unit: "",
  brand: "",
  article: "",
  formal_name: "",
};

/** How the Attributes form should be seeded when its schema next loads. */
type HydrationSeed =
  | { mode: "stored"; attributes: AttributeValueOut[] }
  | { mode: "carry"; from: AttributeFormValues };

/** Statuses from which a seller may still publish (auto-publish → `active`). */
const SUBMITTABLE_STATUSES = new Set(["draft", "rejected"]);

export interface ProductEditorProps {
  port: EditorPort;
  /** `null` means "create a new product" — the wrapper converts its route
   * param's `undefined` to `null` before passing this down. */
  routeId: string | null;
}

export function ProductEditor({ port, routeId }: ProductEditorProps): ReactElement {
  registerSellerDashboardI18n();
  const { t, i18n } = useTranslation(SELLER_DASHBOARD_NS);
  // Unit labels live in the marketplace namespace beside the product page's,
  // so the editor and the buyer read the same word for «шт».
  const { t: tMarketplace } = useTranslation("marketplace");
  const navigate = useNavigate();
  const isNew = routeId === null;
  const { capabilities } = port;

  const {
    productQuery,
    create: createProduct,
    patch: patchProduct,
    reorder: reorderPhotos,
  } = port.useData(routeId);
  const { tree } = useCategoryTree();

  const [productId, setProductId] = useState<string | null>(routeId);
  /** Admin-create only — which seller this new product will belong to. */
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<EditorPhoto[]>([]);
  // SCRUM-168: true while a photo is still uploading/scanning — gates
  // Submit/Save so an in-flight photo can't be silently dropped from the
  // write body (see PhotoUploader's onPendingChange doc comment).
  const [photosPending, setPhotosPending] = useState(false);
  // v0048 vocabulary. Until now `tags` was local state that was never read
  // back from the product and never submitted — a seller typed a term, saw a
  // chip, saved, and it was gone.
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");
  const [folkNames, setFolkNames] = useState<string[]>([]);
  const [folkDraft, setFolkDraft] = useState("");

  // PIM Wave P3 Task 5 — "suggest a missing category/field" dialog. One
  // dialog instance, driven by which kind (if any) is currently open.
  const [suggestKind, setSuggestKind] = useState<"category" | "field" | null>(null);

  // PIM Wave P2 attribute + multi-category state.
  const [extraCategoryIds, setExtraCategoryIds] = useState<string[]>([]);
  const [attrValues, setAttrValues] = useState<AttributeFormValues>({});
  const attrFormRef = useRef<DynamicAttributeFormHandle | null>(null);
  const prevAttrValuesRef = useRef<AttributeFormValues>({});
  const hydrationRef = useRef<HydrationSeed | null>(null);
  // SCRUM-196: the status this product hydrated with — read by `buildBody`'s
  // legacy-`pending_approval` dirty guard below. `null` for a new (unsaved)
  // product, where the guard can never fire.
  const initialStatusRef = useRef<string | null>(null);
  // SCRUM-197: the seller this product hydrated with (or last SAVED as, once
  // a reassignment succeeds) — `buildBody`'s send-when-changed guard below
  // sends `seller_id` ONLY when `sellerId` differs from this. `null` for a
  // new (unsaved) product, where the create path handles `seller_id`
  // separately. Also what the picker's cancel affordance restores to.
  const initialSellerIdRef = useRef<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<ProductEditorForm>({
    resolver: zodResolver(productEditorSchema),
    // Create-mode default status: the admin editor supplies `"active"` so a
    // new product is live on save (auto-publish, REQ-PRD-§11.14-F); the seller
    // editor omits it and keeps the `"draft"` default (publish via the button).
    defaultValues: {
      ...DEFAULTS,
      status: capabilities.defaultCreateStatus ?? DEFAULTS.status,
    },
    mode: "onChange",
  });

  const watched = watch();
  const primaryCategoryId = watched.category_id;

  const formSchemaQuery = useFormSchema(primaryCategoryId || null);
  const schema = formSchemaQuery.data ?? null;

  // PIM Wave P3 Task 5 — resolved primary-category label for the "suggest a
  // category" placement hint. Reuses the already-loaded tree; no new fetch.
  const primaryCategoryLabel = useMemo(() => {
    if (!primaryCategoryId) return null;
    const node = findNodeInTree(tree, primaryCategoryId);
    return node ? categoryDisplayName(node, i18n.language) : null;
  }, [tree, primaryCategoryId, i18n.language]);

  const setPricing = useCallback(
    (field: "price_kgs" | "bulk_price_kgs" | "bulk_threshold", value: string): void => {
      setValue(field, value, { shouldDirty: true });
      void trigger(["price_kgs", "bulk_price_kgs", "bulk_threshold"]);
    },
    [setValue, trigger],
  );

  // Hydrate from the loaded product (edit mode): static fields + photos +
  // extras + a "stored" attribute seed for when the schema loads.
  useEffect(() => {
    const p = productQuery.data;
    if (!p) return;
    reset({
      name: p.name,
      description: p.description ?? "",
      category_id: p.category_id,
      status: p.status,
      stock_state: (["in_stock", "low", "out_of_stock", "unknown"].includes(p.stock_state)
        ? p.stock_state
        : "unknown") as StockState,
      price_kgs: p.price_kgs ?? "",
      bulk_price_kgs: p.bulk_price_kgs ?? "",
      bulk_threshold: p.bulk_threshold !== null ? String(p.bulk_threshold) : "",
      unit: asUnitSlug(p.unit),
      brand: p.brand ?? "",
      article: p.article ?? "",
      formal_name: p.formal_name ?? "",
    });
    setProductId(p.id);
    initialStatusRef.current = p.status;
    // SCRUM-197: hydrate both harmlessly on the seller surface — its
    // capabilities never render the picker and the `buildBody` guard below
    // never emits the key regardless of what `sellerId` holds.
    setSellerId(p.seller_id);
    initialSellerIdRef.current = p.seller_id;
    setTags(p.tags ?? []);
    setFolkNames(p.folk_names ?? []);
    setPhotos(
      p.images.map((img) => ({
        id: img.id,
        imageId: img.id,
        s3KeyOriginal: img.s3_key_original,
        previewUrl: img.thumb_url,
      })),
    );
    // Extras = all category ids except the primary.
    setExtraCategoryIds((p.category_ids ?? []).filter((cid) => cid !== p.category_id));
    // Seed the attribute form from the stored values once its schema loads.
    hydrationRef.current = { mode: "stored", attributes: p.attributes ?? [] };
  }, [productQuery.data, reset]);

  // Initial values for the (re)mounted attribute form — read at mount only
  // (the form is keyed by `primaryCategoryId`, so this is applied exactly on a
  // category change / first load, not on a same-category refetch).
  const attrInitial = useMemo<AttributeFormValues>(() => {
    if (!schema) return {};
    const h = hydrationRef.current;
    if (h?.mode === "stored") return initialFormValues(schema, h.attributes);
    if (h?.mode === "carry") return carryOverValues(h.from, schema).initialValues;
    return initialFormValues(schema, []);
  }, [schema]);

  // Once the schema is present, warn about any carried-over values being
  // dropped, then consume the seed so a later same-category refetch is inert.
  useEffect(() => {
    if (!schema) return;
    const h = hydrationRef.current;
    if (h?.mode === "carry") {
      const { droppedSlugs } = carryOverValues(h.from, schema);
      if (droppedSlugs.length > 0) {
        toast(t("editor.category.droppedFields", { count: droppedSlugs.length }));
      }
    }
    hydrationRef.current = null;
  }, [schema, t]);

  const handleAttrChange = useCallback((values: AttributeFormValues): void => {
    prevAttrValuesRef.current = values;
    setAttrValues(values);
  }, []);

  const handlePrimaryChange = useCallback(
    (id: string): void => {
      // Carry forward whatever is entered into the next schema.
      hydrationRef.current = { mode: "carry", from: prevAttrValuesRef.current };
      setExtraCategoryIds((cur) => cur.filter((cid) => cid !== id));
      setValue("category_id", id, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const handlePhotoReady = useCallback((photo: ReadyPhoto): void => {
    setPhotos((cur) => [
      ...cur,
      {
        id: photo.s3KeyClean,
        imageId: null,
        s3KeyOriginal: photo.s3KeyClean,
        previewUrl: photo.previewUrl,
      },
    ]);
  }, []);

  const handleRemovePhoto = useCallback((photoId: string): void => {
    setPhotos((cur) => cur.filter((p) => p.id !== photoId));
  }, []);

  const hydratePhotosFromServer = useCallback(
    (saved: { images: EditorProduct["images"] }): void => {
      setPhotos(
        saved.images.map((img) => ({
          id: img.id,
          imageId: img.id,
          s3KeyOriginal: img.s3_key_original,
          previewUrl: img.thumb_url,
        })),
      );
    },
    [],
  );

  const handleReorder = useCallback(
    (orderedIds: string[]): void => {
      const snapshot = photos;
      const byId = new Map(snapshot.map((p) => [p.id, p]));
      const next = orderedIds
        .map((id) => byId.get(id))
        .filter((p): p is EditorPhoto => p !== undefined);
      setPhotos(next);
      if (productId !== null) {
        reorderPhotos.mutate(
          { productId, orderedImageIds: orderedIds },
          {
            onError: () => {
              setPhotos(snapshot);
              toast.error(t("photos.reorderFailed"));
            },
          },
        );
      }
    },
    [photos, productId, reorderPhotos, t],
  );

  // ── 403 on edit-mode load → capabilities.forbiddenRedirectTo + toast. (After
  // all hooks.) ──────────────────────────────────────────────────────────────
  const loadError = productQuery.error;
  if (
    capabilities.forbiddenRedirectTo !== undefined &&
    loadError instanceof ApiError &&
    loadError.status === 403
  ) {
    toast.error(t("forbidden"));
    return <Navigate to={capabilities.forbiddenRedirectTo} replace />;
  }

  const buildAttributesPayload = (): AttributeValueIn[] | undefined => {
    if (!schema) return undefined; // no schema loaded → leave attributes untouched
    const current = attrFormRef.current?.getValues() ?? attrValues;
    return toAttributePayload(schema, current, visibleFieldSlugs(schema, current));
  };

  const buildBody = (form: ProductEditorForm, status: string): EditorWriteBody => {
    const attributes = buildAttributesPayload();
    const categoryIds = form.category_id
      ? Array.from(new Set([form.category_id, ...extraCategoryIds]))
      : undefined;
    // SCRUM-196: a legacy `pending_approval` row (persisted before the admin
    // status control stopped offering that value) must not be silently
    // rewritten by an UNRELATED save. The disabled option in
    // `AdminStatusSelect` means `form.status` can only still read
    // "pending_approval" here if the admin never touched the control — i.e.
    // this save carries no status intent at all. Sending the value would
    // have `resolve_write_status` (service.py) rewrite it to "active",
    // publishing the row without the admin choosing to. Omitting the key
    // instead leaves it untouched: `EditorWriteBody.status` is optional and
    // the server's `exclude_unset=True` treats an absent key as "leave
    // alone". Scoped to `statusControl === "direct"` — the seller's
    // submit-review footer never sets status to "pending_approval" via this
    // path in the first place, and must stay unaffected.
    const legacyPendingUntouched =
      capabilities.statusControl === "direct" &&
      initialStatusRef.current === "pending_approval" &&
      status === "pending_approval";
    // SCRUM-197: send-when-changed, NOT whole-form [review B2]. This PATCH
    // has no optimistic-concurrency guard (no If-Match, no version field),
    // so a whole-form send from a stale admin tab would silently reassign
    // ownership backwards AND write an audit row attributing a deliberate
    // reassignment to the stale admin — moving a revenue-bearing row
    // between businesses on a save nobody intended. `seller_id` was never a
    // whole-form field to begin with (it lives outside RHF in `sellerId`
    // state, create-only in `EditorCreateBody`) — this extends the same
    // convention to edit mode. `sellerId !== null` stays load-bearing:
    // hydration-not-yet-run must omit the key, never send `null` (the
    // column is NOT NULL — `ProductUpdate` would accept `null` and the
    // write would then 500). The null-check is inlined directly at the
    // spread site below (rather than hoisted to a boolean) so TypeScript
    // narrows `sellerId` to `string` inside the ternary's true branch.
    return {
      name: form.name,
      description: form.description === "" ? null : form.description,
      category_id: form.category_id,
      ...(legacyPendingUntouched ? {} : { status }),
      stock_state: form.stock_state,
      price_kgs: form.price_kgs.trim() === "" ? null : normaliseDecimal(form.price_kgs),
      bulk_price_kgs:
        form.bulk_price_kgs.trim() === "" ? null : normaliseDecimal(form.bulk_price_kgs),
      bulk_threshold:
        form.bulk_threshold.trim() === "" ? null : Number(form.bulk_threshold.trim()),
      photos: photos.map((p, i) => ({ s3_key_original: p.s3KeyOriginal, sort_order: i })),
      ...(attributes !== undefined ? { attributes } : {}),
      ...(categoryIds
        ? { category_ids: categoryIds, primary_category_id: form.category_id }
        : {}),
      ...(capabilities.showSellerPicker &&
      sellerId !== null &&
      sellerId !== initialSellerIdRef.current
        ? { seller_id: sellerId }
        : {}),
      // Blank means "no value", not "leave alone" — the editor always shows
      // the whole product, so an empty box IS the seller clearing the field.
      unit: form.unit === "" ? null : form.unit,
      brand: form.brand.trim() === "" ? null : form.brand.trim(),
      article: form.article.trim() === "" ? null : form.article.trim(),
      formal_name: form.formal_name.trim() === "" ? null : form.formal_name.trim(),
      tags,
      folk_names: folkNames,
    };
  };

  const handleWriteError = (err: unknown): void => {
    const pim = parsePimError(err);
    if (pim) {
      attrFormRef.current?.setServerErrors(pim.fields);
      toast.error(
        pim.code === "pim.required_missing"
          ? t("editor.attributes.requiredMissing")
          : t("editor.attributes.invalid"),
      );
      return;
    }
    // Surface the server's specific reason (RFC-9457 `detail`) for a client
    // (4xx) validation failure — e.g. "A product may only be attached to a leaf
    // category" — instead of a blank "Failed to save product" that stranded the
    // seller with no way to know what to fix. 5xx / network keep the generic
    // toast (no useful server detail to show).
    if (
      err instanceof ApiError &&
      err.status >= 400 &&
      err.status < 500 &&
      err.message.trim() !== ""
    ) {
      toast.error(err.message);
      return;
    }
    toast.error(t("editor.saveFailed"));
  };

  // Validate static + dynamic, then create or patch with the target status.
  const submitWith = async (form: ProductEditorForm, status: string): Promise<void> => {
    if (capabilities.showSellerPicker && productId === null && sellerId === null) {
      // The seller-picker widget communicates "required" on its own;
      // `submitDisabled` below also keeps the button disabled until chosen.
      return;
    }
    const attrOk = (await attrFormRef.current?.validate()) ?? true;
    if (!attrOk) return; // inline attribute errors are already shown
    const body = buildBody(form, status);

    if (productId === null) {
      const createBody: EditorCreateBody = {
        category_id: body.category_id!,
        name: body.name!,
        description: body.description,
        price_kgs: body.price_kgs,
        bulk_price_kgs: body.bulk_price_kgs,
        bulk_threshold: body.bulk_threshold,
        stock_state: body.stock_state,
        status: body.status,
        attributes: body.attributes,
        category_ids: body.category_ids,
        primary_category_id: body.primary_category_id,
        ...(capabilities.showSellerPicker && sellerId !== null
          ? { seller_id: sellerId }
          : {}),
      };
      createProduct.mutate(createBody, {
        onSuccess: (created) => {
          setProductId(created.id);
          if (photos.length > 0) {
            patchProduct.mutate(
              { id: created.id, body: { photos: body.photos } },
              {
                onSuccess: (saved) => {
                  hydratePhotosFromServer(saved);
                  toast.success(t("editor.saved"));
                  navigate(capabilities.productPathAfterCreate(created.id), {
                    replace: true,
                  });
                },
                onError: () => toast.error(t("editor.saveFailed")),
              },
            );
          } else {
            toast.success(t("editor.saved"));
            navigate(capabilities.productPathAfterCreate(created.id), {
              replace: true,
            });
          }
        },
        onError: handleWriteError,
      });
    } else {
      patchProduct.mutate(
        { id: productId, body },
        {
          onSuccess: (saved) => {
            hydratePhotosFromServer(saved);
            // SCRUM-197: refresh the baseline a reassignment is measured
            // against, so a FOLLOW-UP save doesn't re-send an
            // already-applied `seller_id` change as if it were new.
            initialSellerIdRef.current = saved.seller_id;
            toast.success(t("editor.saved"));
          },
          onError: handleWriteError,
        },
      );
    }
  };

  const isSaving = createProduct.isPending || patchProduct.isPending;
  const thumbUrl = photos[0]?.previewUrl ?? null;

  const requiredMissingCount = schema
    ? requiredMissingFields(schema, attrValues).length
    : 0;
  const canSubmitForReview = SUBMITTABLE_STATUSES.has(watched.status);
  const submitDisabled =
    isSaving ||
    photosPending ||
    (capabilities.statusControl === "submit-review" && requiredMissingCount > 0) ||
    (capabilities.showSellerPicker && productId === null && sellerId === null);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6" data-testid="product-editor-page">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to={capabilities.listPath}
            aria-label={t("editor.back")}
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-[var(--g-radius)] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon name="chevL" size={20} />
          </Link>
          <h1 className="truncate text-xl font-bold text-foreground">
            {capabilities.pageTitle(isNew)}
          </h1>
        </div>
        <ThemeToggle />
      </div>

      {/* Completeness (live estimate) — only once a category schema is present. */}
      <div className="mb-4">
        <CompletenessMeter
          schema={schema}
          values={attrValues}
          language={i18n.language}
          onJumpToField={(slug) => attrFormRef.current?.focusField(slug)}
        />
      </div>

      <form
        onSubmit={handleSubmit((form) => void submitWith(form, form.status))}
        className="grid gap-6 lg:grid-cols-[1fr_320px]"
        data-testid="editor-form"
      >
        {/* Left: editable form */}
        <div className="flex flex-col gap-6">
          {/* Basics */}
          <section aria-labelledby="sec-basics" className="flex flex-col gap-3">
            <h2 id="sec-basics" className="text-sm font-semibold text-foreground">
              {t("editor.sections.basics")}
            </h2>
            {/* SCRUM-197: admin create AND edit (the `isNew` gate that used
             * to hide this in edit mode is deleted — that was the bug: the
             * Edit form had no way to see or change the seller at all). */}
            {capabilities.showSellerPicker
              ? capabilities.renderSellerPicker?.(sellerId, setSellerId, {
                  reassigning: !isNew,
                  initialSellerId: initialSellerIdRef.current,
                })
              : null}
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-sm font-medium text-foreground">
                {t("editor.basics.name")}
              </label>
              <input
                id="name"
                data-testid="name-input"
                {...register("name")}
                placeholder={t("editor.basics.namePlaceholder")}
                aria-invalid={errors.name ? true : undefined}
                className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="description" className="text-sm font-medium text-foreground">
                {t("editor.basics.description")}
              </label>
              <textarea
                id="description"
                data-testid="description-input"
                {...register("description")}
                rows={3}
                placeholder={t("editor.basics.descriptionPlaceholder")}
                className="rounded-[var(--g-radius)] border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
              />
            </div>
            {/* v0048 workbook properties. Unit sits with pricing rather than
                here in a later pass would be wrong: it qualifies the PRICE,
                but the seller thinks of it as a fact about the product, and
                the API validates it as one. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="unit" className="text-sm font-medium text-foreground">
                  {t("editor.basics.unit")}
                </label>
                <select
                  id="unit"
                  data-testid="unit-select"
                  {...register("unit")}
                  className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
                >
                  <option value="">{t("editor.basics.unitUnset")}</option>
                  {PRODUCT_UNIT_SLUGS.map((slug) => (
                    <option key={slug} value={slug}>
                      {tMarketplace(`unit.${slug}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="brand" className="text-sm font-medium text-foreground">
                  {t("editor.basics.brand")}
                </label>
                <input
                  id="brand"
                  data-testid="brand-input"
                  {...register("brand")}
                  className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="article" className="text-sm font-medium text-foreground">
                  {t("editor.basics.article")}
                </label>
                <input
                  id="article"
                  data-testid="article-input"
                  {...register("article")}
                  className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="formal_name"
                  className="text-sm font-medium text-foreground"
                >
                  {t("editor.basics.formalName")}
                </label>
                <input
                  id="formal_name"
                  data-testid="formal-name-input"
                  {...register("formal_name")}
                  className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
                />
              </div>
            </div>

            {/* Category (primary + extras) */}
            <CategoryMultiPicker
              tree={tree}
              language={i18n.language}
              primaryId={primaryCategoryId}
              extraIds={extraCategoryIds}
              onPrimaryChange={handlePrimaryChange}
              onExtrasChange={setExtraCategoryIds}
              primaryError={errors.category_id ? t("editor.category.required") : undefined}
            />
            {capabilities.showSuggestTriggers ? (
              <button
                type="button"
                onClick={() => setSuggestKind("category")}
                data-testid="suggest-category-trigger"
                className="self-start text-xs font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("suggestions.category.trigger")}
              </button>
            ) : null}
          </section>

          {/* Pricing */}
          <section aria-labelledby="sec-pricing" className="flex flex-col gap-3">
            <h2 id="sec-pricing" className="text-sm font-semibold text-foreground">
              {t("editor.sections.pricing")}
            </h2>
            <PricingFields
              price={watched.price_kgs}
              bulkPrice={watched.bulk_price_kgs}
              bulkThreshold={watched.bulk_threshold}
              onPriceChange={(v) => setPricing("price_kgs", v)}
              onBulkPriceChange={(v) => setPricing("bulk_price_kgs", v)}
              onBulkThresholdChange={(v) => setPricing("bulk_threshold", v)}
              priceError={errors.price_kgs ? t(errors.price_kgs.message ?? "") : undefined}
              bulkPriceError={
                errors.bulk_price_kgs ? t(errors.bulk_price_kgs.message ?? "") : undefined
              }
              bulkThresholdError={
                errors.bulk_threshold ? t(errors.bulk_threshold.message ?? "") : undefined
              }
            />
          </section>

          {/* Stock */}
          <section aria-labelledby="sec-stock" className="flex flex-col gap-3">
            <h2 id="sec-stock" className="sr-only">
              {t("editor.sections.stock")}
            </h2>
            <StockStateField
              value={watched.stock_state}
              onChange={(v) => setValue("stock_state", v, { shouldValidate: true })}
            />
          </section>

          {/* Attributes (schema-driven) */}
          <section aria-labelledby="sec-attributes" className="flex flex-col gap-3">
            <h2 id="sec-attributes" className="text-sm font-semibold text-foreground">
              {t("editor.sections.attributes")}
            </h2>
            {!primaryCategoryId ? (
              <p className="text-sm text-muted-foreground">
                {t("editor.attributes.pickCategory")}
              </p>
            ) : formSchemaQuery.isLoading ? (
              <div
                role="status"
                aria-label={t("loading")}
                className="flex justify-center py-6"
              >
                <span
                  aria-hidden="true"
                  className="size-6 animate-spin rounded-full border-2 border-[var(--g-hairline)] border-t-[var(--g-primary)]"
                />
              </div>
            ) : formSchemaQuery.isError ? (
              <AttributesError error={formSchemaQuery.error} onRetry={() => void formSchemaQuery.refetch()} />
            ) : schema && schema.groups.some((g) => g.fields.length > 0) ? (
              <DynamicAttributeForm
                key={primaryCategoryId}
                ref={attrFormRef}
                schema={schema}
                initialValues={attrInitial}
                language={i18n.language}
                onChange={handleAttrChange}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("editor.attributes.none")}
              </p>
            )}
            {capabilities.showSuggestTriggers ? (
              <button
                type="button"
                onClick={() => setSuggestKind("field")}
                data-testid="suggest-field-trigger"
                className="self-start text-xs font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("suggestions.field.trigger")}
              </button>
            ) : null}
          </section>

          {/* Photos */}
          <section aria-labelledby="sec-photos" className="flex flex-col gap-3">
            <h2 id="sec-photos" className="text-sm font-semibold text-foreground">
              {t("editor.sections.photos")}
            </h2>
            <PhotoUploader
              onPhotoReady={handlePhotoReady}
              onPendingChange={setPhotosPending}
            />
            {photosPending ? (
              <p
                className="text-xs text-muted-foreground"
                role="status"
                data-testid="photos-pending-hint"
              >
                {t("photos.pendingHint")}
              </p>
            ) : null}
            <PhotoReorderList
              photos={photos}
              onReorder={handleReorder}
              onRemove={handleRemovePhoto}
            />
          </section>

          {/* Variants */}
          {capabilities.showVariants ? (
            <section aria-labelledby="sec-variants" className="flex flex-col gap-3">
              <h2 id="sec-variants" className="text-sm font-semibold text-foreground">
                {t("editor.sections.variants")}
              </h2>
              <VariantEditor productId={productId} variants={port.variants} />
            </section>
          ) : null}

          {/* Vocabulary — «Теги» and «Народные названия».
              Two registers, kept separate on purpose: a folk name is the
              buyer's own word (83% of them name exactly ONE product) while a
              tag is the cataloguer's keyword and its head is class vocabulary
              («металл» names 63). Merging them would lose the distinction that
              makes per-product retrieval evidence sound.

              This whole section used to be local state that was never loaded
              and never submitted. */}
          <section aria-labelledby="sec-tags" className="flex flex-col gap-4">
            <h2 id="sec-tags" className="text-sm font-semibold text-foreground">
              {t("editor.sections.tags")}
            </h2>
            <TermEditor
              label={t("editor.tags.label")}
              hint={t("editor.tags.hint")}
              placeholder={t("editor.tags.placeholder")}
              addLabel={t("editor.tags.add")}
              removeLabel={t("list.delete")}
              testId="tag"
              terms={tags}
              draft={tagDraft}
              onDraftChange={setTagDraft}
              onChange={setTags}
            />
            <TermEditor
              label={t("editor.folkNames.label")}
              hint={t("editor.folkNames.hint")}
              placeholder={t("editor.folkNames.placeholder")}
              addLabel={t("editor.tags.add")}
              removeLabel={t("list.delete")}
              testId="folk"
              terms={folkNames}
              draft={folkDraft}
              onDraftChange={setFolkDraft}
              onChange={setFolkNames}
            />
          </section>

          {/* Footer actions */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {capabilities.statusControl === "submit-review" ? (
              <>
                {canSubmitForReview ? (
                  <button
                    type="button"
                    disabled={submitDisabled}
                    title={
                      requiredMissingCount > 0
                        ? t("editor.submit.disabledReason")
                        : photosPending
                          ? t("photos.pendingHint")
                          : undefined
                    }
                    onClick={() =>
                      void handleSubmit((form) =>
                        // Auto-publish (REQ-PRD-§11.14-F): publishing goes live
                        // immediately (`active`) — no admin-approval step. The
                        // button stays disabled until required attributes are
                        // filled (the client mirror of the backend submit gate).
                        void submitWith(form, "active"),
                      )()
                    }
                    data-testid="editor-publish"
                    className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary px-6 text-sm font-semibold text-[var(--g-on-primary)] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t("editor.submit.publish")}
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={isSaving || photosPending}
                  data-testid="editor-save"
                  className={[
                    "inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] px-6 text-sm font-semibold disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    canSubmitForReview
                      ? "border border-border text-foreground"
                      : "bg-primary text-[var(--g-on-primary)]",
                  ].join(" ")}
                >
                  {isSaving
                    ? t("editor.saving")
                    : canSubmitForReview
                      ? t("editor.submit.saveDraft")
                      : t("editor.save")}
                </button>
              </>
            ) : (
              <>
                {capabilities.renderStatusControl?.(watched.status, (v) =>
                  setValue("status", v, { shouldValidate: true, shouldDirty: true }),
                )}
                <button
                  type="submit"
                  disabled={submitDisabled}
                  data-testid="editor-save"
                  className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary px-6 text-sm font-semibold text-[var(--g-on-primary)] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {isSaving ? t("editor.saving") : t("editor.save")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right: live preview (sticky on desktop).
            SCRUM-166: decorative (`aria-hidden`) preview column is desktop-only
            by design — below `lg` the grid collapses to one column (no
            `lg:grid-cols`), so this block used to stack full-width right after
            the Save/Draft buttons. Its `aspect-square` "no photo" placeholder
            then rendered as a large blank square spanning the phone's full
            width, which read as empty space below the form controls on
            scroll. `hidden lg:block` removes it from the mobile flow entirely
            (pure layout — no logic/data change); desktop keeps the exact
            prior sticky behavior. */}
        <div className="hidden lg:block lg:sticky lg:top-4 lg:self-start">
          <LivePreview
            name={watched.name}
            price={watched.price_kgs}
            stockState={watched.stock_state}
            thumbUrl={thumbUrl}
          />
        </div>
      </form>

      {/* PIM Wave P3 Task 5 — one dialog instance, two triggers above. */}
      {capabilities.showSuggestTriggers ? (
        <SuggestDialog
          open={suggestKind !== null}
          onClose={() => setSuggestKind(null)}
          kind={suggestKind ?? "category"}
          parentCategoryId={primaryCategoryId || null}
          parentCategoryLabel={primaryCategoryLabel}
        />
      ) : null}
    </div>
  );
}

/** Attributes-section error: 404 not-found / 409 archived / generic + retry. */

/**
 * One vocabulary register: chips plus an add box.
 *
 * Extracted because there are two of them and they must behave identically —
 * the previous single inline copy was also, incidentally, the thing that never
 * saved. Terms are trimmed and de-duplicated here so the API never has to
 * decide whether «Труба» and «труба » are one term (it folds them anyway, and
 * a chip appearing twice looks broken).
 */
function TermEditor({
  label,
  hint,
  placeholder,
  addLabel,
  removeLabel,
  testId,
  terms,
  draft,
  onDraftChange,
  onChange,
}: {
  label: string;
  hint: string;
  placeholder: string;
  addLabel: string;
  removeLabel: string;
  testId: string;
  terms: string[];
  draft: string;
  onDraftChange: (value: string) => void;
  onChange: (terms: string[]) => void;
}): ReactElement {
  const add = (): void => {
    const value = draft.trim();
    if (value === "") return;
    if (terms.some((term) => term.toLowerCase() === value.toLowerCase())) {
      onDraftChange("");
      return;
    }
    onChange([...terms, value]);
    onDraftChange("");
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <p className="text-xs text-muted-foreground">{hint}</p>
      {terms.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid={`${testId}-chips`}>
          {terms.map((term) => (
            <span
              key={term}
              className="inline-flex h-8 items-center gap-1.5 rounded-full bg-secondary px-3 text-sm text-secondary-foreground"
            >
              {term}
              <button
                type="button"
                onClick={() => onChange(terms.filter((x) => x !== term))}
                aria-label={`${removeLabel}: ${term}`}
              >
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          aria-label={label}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          // `min-w-0`: a `flex-1` item still refuses to shrink below its
          // min-content width, so the placeholder alone pushed this row (and
          // the page) past 320. `text-base` below `md` keeps iOS from zooming.
          className="min-h-[44px] min-w-0 flex-1 rounded-[var(--g-radius)] border border-border bg-card px-3 text-base text-foreground outline-none md:text-sm"
        />
        <button
          type="button"
          onClick={add}
          data-testid={`${testId}-add`}
          className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] border border-border px-4 text-sm font-semibold text-foreground"
        >
          {addLabel}
        </button>
      </div>
    </div>
  );
}


function AttributesError({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const code = error instanceof ApiError ? error.code : undefined;
  const message =
    code === "catalog_schema.category.archived"
      ? t("editor.attributes.categoryArchived")
      : code === "catalog_schema.category.not_found"
        ? t("editor.attributes.categoryNotFound")
        : t("editor.attributes.loadFailed");
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-2 rounded-[var(--g-radius)] border border-[var(--g-error)]/40 bg-[var(--g-error)]/5 p-4"
    >
      <p className="text-sm text-muted-foreground">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] border border-border px-4 text-sm font-semibold text-foreground"
      >
        {t("editor.attributes.retry")}
      </button>
    </div>
  );
}

export default ProductEditor;
